import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Phone, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";
import { MISSION_STATUS_LABELS, formatDate } from "@/constants/business_status";

const STATUS_COLORS = {
  provisional: "bg-amber-100 text-amber-800", confirmed: "bg-green-100 text-green-800",
  completed: "bg-gray-200 text-gray-700", incident: "bg-red-100 text-red-700",
};

function fmtWeek(d) { return d.toISOString().slice(0, 10); }

export function PlanningPage() {
  const [anchor, setAnchor] = useState(new Date());
  const [view, setView] = useState("week");
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const range = (() => {
    const a = new Date(anchor);
    if (view === "day") return [fmtWeek(a), fmtWeek(a)];
    if (view === "month") {
      const s = new Date(a.getFullYear(), a.getMonth(), 1);
      const e = new Date(a.getFullYear(), a.getMonth() + 1, 0);
      return [fmtWeek(s), fmtWeek(e)];
    }
    const day = (a.getDay() + 6) % 7;
    const s = new Date(a); s.setDate(a.getDate() - day);
    const e = new Date(s); e.setDate(s.getDate() + 6);
    return [fmtWeek(s), fmtWeek(e)];
  })();

  useEffect(() => {
    apiClient.get("/admin/planning", { params: { start: range[0], end: range[1] } })
      .then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (dir) => {
    const a = new Date(anchor);
    a.setDate(a.getDate() + dir * (view === "day" ? 1 : view === "week" ? 7 : 30));
    setAnchor(a);
  };

  const days = [];
  for (let d = new Date(range[0]); fmtWeek(d) <= range[1]; d.setDate(d.getDate() + 1)) days.push(fmtWeek(new Date(d)));

  return (
    <AdminGuard>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="font-heading font-extrabold text-2xl text-navy mr-auto">Planning</h1>
        {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"], ["list", "Liste"]].map(([k, l]) => (
          <button key={k} data-testid={`planning-view-${k}`} onClick={() => setView(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${view === k ? "bg-navy text-white" : "bg-white text-navy"}`}>{l}</button>
        ))}
        <Button variant="outline" size="sm" className="rounded-lg" data-testid="planning-prev" onClick={() => shift(-1)}>←</Button>
        <span className="text-sm font-semibold text-navy">{range[0]} → {range[1]}</span>
        <Button variant="outline" size="sm" className="rounded-lg" data-testid="planning-next" onClick={() => shift(1)}>→</Button>
      </div>
      {!data ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div> : (
        <>
          {data.conflicts.length > 0 && (
            <div data-testid="planning-conflicts" className="bg-red-50 text-red-800 rounded-xl px-4 py-2.5 mb-4 text-sm">
              ⚠ {data.conflicts.length} conflit(s) : {data.conflicts.map((c) => `${c.date} (${c.type} : ${c.missions.join(" / ")})`).join(" • ")}
            </div>
          )}
          {view === "list" ? (
            <div className="bg-white rounded-2xl shadow-sm divide-y">
              {data.missions.length === 0 && <div className="py-10 text-center text-muted-foreground text-sm">Aucune mission sur la période</div>}
              {data.missions.map((m) => (
                <button key={m.id} data-testid={`planning-mission-${m.id}`} onClick={() => navigate(`/admin/missions/${m.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                  <span className="text-sm font-bold text-navy w-24">{m.date}</span>
                  <span className="text-xs w-14">{m.start_time || ""}</span>
                  <span className="flex-1 text-sm font-medium">{m.reference} — {m.case_number}</span>
                  <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_COLORS[m.status] || "bg-navy/5 text-navy"}`}>{MISSION_STATUS_LABELS[m.status]}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className={`grid gap-2 ${view === "day" ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4 lg:grid-cols-7"}`}>
              {days.map((d) => {
                const dayMissions = data.missions.filter((m) => m.date === d);
                return (
                  <div key={d} className="bg-white rounded-xl shadow-sm p-2 min-h-[90px]" data-testid={`planning-day-${d}`}>
                    <div className="text-[11px] font-bold text-muted-foreground mb-1">{new Date(d).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</div>
                    {dayMissions.map((m) => (
                      <button key={m.id} onClick={() => navigate(`/admin/missions/${m.id}`)}
                        className={`w-full text-left text-[11px] rounded-lg px-2 py-1 mb-1 font-semibold ${STATUS_COLORS[m.status] || "bg-navy/10 text-navy"}`}>
                        {m.start_time || ""} {m.reference}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </AdminGuard>
  );
}

export function MissionsPage() {
  const [items, setItems] = useState(null);
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState({ case_id: "", date: "", start_time: "", status: "provisional" });
  const navigate = useNavigate();
  const load = useCallback(() => {
    apiClient.get("/admin/missions", { params: { page_size: 100 } }).then((r) => setItems(r.data.items)).catch(() => {});
    apiClient.get("/admin/cases", { params: { page_size: 100, status: "accepted" } }).then((r) => setCases(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (override) => {
    if (!form.case_id || !form.date) return toast.error("Dossier et date requis");
    try {
      await apiClient.post("/admin/missions", { ...form, override_justification: override || undefined });
      toast.success("Mission créée"); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.conflicts) {
        const j = window.prompt(`Conflits :\n${det.conflicts.join("\n")}\n\nJustification pour passer outre (vide = annuler)`);
        if (j) return create(j);
      } else toast.error(formatApiErrorDetail(det));
    }
  };

  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Missions</h1>
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="mission-form">
        <div className="min-w-[220px]">
          <label className="text-[11px] font-semibold text-navy">Dossier accepté</label>
          <Select value={form.case_id} onValueChange={(v) => setForm((p) => ({ ...p, case_id: v }))}>
            <SelectTrigger data-testid="mission-case" className="h-9 bg-white rounded-lg"><SelectValue placeholder="Choisir un dossier" /></SelectTrigger>
            <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.contact_name || c.service_type || ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-[11px] font-semibold text-navy">Date</label>
          <Input data-testid="mission-date" type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="h-9 bg-white rounded-lg" /></div>
        <div><label className="text-[11px] font-semibold text-navy">Heure</label>
          <Input data-testid="mission-time" type="time" value={form.start_time} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))} className="h-9 bg-white rounded-lg" /></div>
        <Button data-testid="mission-submit" onClick={() => create()} className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" /> Planifier</Button>
      </div>
      {items === null ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div> : (
        <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="missions-list">
          {items.length === 0 && <div className="py-10 text-center text-muted-foreground text-sm">Aucune mission</div>}
          {items.map((m) => (
            <button key={m.id} data-testid={`mission-row-${m.id}`} onClick={() => navigate(`/admin/missions/${m.id}`)}
              className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
              <span className="font-bold text-navy text-sm">{m.reference}</span>
              <span className="text-sm">{m.case_number}</span>
              <span className="text-xs text-muted-foreground mr-auto">{m.date} {m.start_time || ""}</span>
              <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_COLORS[m.status] || "bg-navy/5 text-navy"}`}>{MISSION_STATUS_LABELS[m.status]}</span>
            </button>
          ))}
        </div>
      )}
    </AdminGuard>
  );
}

export function MissionDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiClient.get(`/admin/missions/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Mission introuvable"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <AdminGuard><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminGuard>;
  const { mission, contact, vehicle, team } = data;
  const patch = async (updates) => {
    try { await apiClient.patch(`/admin/missions/${id}`, updates); toast.success("Mission mise à jour"); load(); }
    catch (e) {
      const det = e.response?.data?.detail;
      if (det?.conflicts) {
        const j = window.prompt(`Conflits :\n${det.conflicts.join("\n")}\n\nJustification (vide = annuler)`);
        if (j) return patch({ ...updates, override_justification: j });
      } else toast.error(formatApiErrorDetail(det));
    }
  };
  const toggleCheck = (key) => patch({ checklist: { ...(mission.checklist || {}), [key]: !(mission.checklist || {})[key] } });

  return (
    <AdminGuard>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 data-testid="mission-detail-header" className="font-heading font-extrabold text-2xl text-navy mr-auto">{mission.reference}</h1>
        <span className="text-xs font-bold bg-navy/5 text-navy rounded-full px-3 py-1.5">{MISSION_STATUS_LABELS[mission.status]}</span>
        <Select value={mission.status} onValueChange={(s) => patch({ status: s })}>
          <SelectTrigger data-testid="mission-status-select" className="h-9 w-44 bg-white rounded-lg text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(MISSION_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Informations</h3>
          <div className="text-sm space-y-1">
            <div>Dossier : {data.case ? <Link className="underline" to={`/admin/dossiers/${data.case.id}`}>{mission.case_number}</Link> : mission.case_number}</div>
            <div>Client : <strong>{contact?.display_name || "—"}</strong> {contact?.phone && `• ${contact.phone}`}</div>
            <div className="flex gap-2 items-center">Date :
              <Input data-testid="mission-edit-date" type="date" defaultValue={mission.date || ""} onBlur={(e) => e.target.value !== mission.date && patch({ date: e.target.value })} className="h-8 w-40 bg-white" />
              <Input data-testid="mission-edit-time" type="time" defaultValue={mission.start_time || ""} onBlur={(e) => e.target.value !== mission.start_time && patch({ start_time: e.target.value })} className="h-8 w-28 bg-white" />
            </div>
            <div>Véhicule : {vehicle ? `${vehicle.registration} (${vehicle.category})` : "Non affecté"}</div>
            <div>Équipe : {team?.name || "Non affectée"}</div>
          </div>
          <AssignSelectors mission={mission} patch={patch} />
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Checklist avant mission</h3>
          {Object.entries(data.checklist_labels).map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`mission-check-${k}`}>
              <input type="checkbox" checked={(mission.checklist || {})[k] || false} onChange={() => toggleCheck(k)} />{l}
            </label>
          ))}
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Suivi terrain</h3>
          {(mission.field_events || []).length === 0 ? <div className="text-xs text-muted-foreground">Aucun événement</div> :
            mission.field_events.map((e, i) => (
              <div key={i} className="text-xs py-1 border-b last:border-0"><strong>{e.action}</strong> — {new Date(e.at).toLocaleString("fr-FR")} ({e.by})</div>
            ))}
          {(mission.history || []).filter((h) => h.comment).slice(-3).map((h, i) => (
            <div key={i} className="text-xs text-muted-foreground pt-1">{h.comment}</div>
          ))}
        </div>
      </div>
    </AdminGuard>
  );
}

function AssignSelectors({ mission, patch }) {
  const [vehicles, setVehicles] = useState([]);
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    apiClient.get("/admin/vehicles", { params: { page_size: 100 } }).then((r) => setVehicles(r.data.items)).catch(() => {});
    apiClient.get("/admin/teams", { params: { page_size: 100 } }).then((r) => setTeams(r.data.items)).catch(() => {});
  }, []);
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      <Select value={mission.vehicle_id || ""} onValueChange={(v) => patch({ vehicle_id: v })}>
        <SelectTrigger data-testid="mission-assign-vehicle" className="h-9 w-44 bg-white rounded-lg text-xs"><SelectValue placeholder="Affecter un véhicule" /></SelectTrigger>
        <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={mission.team_id || ""} onValueChange={(v) => patch({ team_id: v })}>
        <SelectTrigger data-testid="mission-assign-team" className="h-9 w-44 bg-white rounded-lg text-xs"><SelectValue placeholder="Affecter une équipe" /></SelectTrigger>
        <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

const FIELD_ACTION_LABELS = {
  accepted: "Mission acceptée", departed: "Départ", arrived: "Arrivée sur place",
  loading_started: "Début chargement", loading_done: "Fin chargement",
  transport_started: "Départ transport", arrived_destination: "Arrivée destination",
  unloading_started: "Début déchargement", completed: "Mission terminée",
  incident_reported: "Signaler un incident",
};

export function FieldPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/admin/field/today").then((r) => setData(r.data)).catch(() => toast.error("Erreur"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (missionId, action) => {
    let comment;
    if (action === "incident_reported") {
      comment = window.prompt("Décrivez l'incident");
      if (!comment) return;
      await apiClient.post("/admin/incidents", { mission_id: missionId, incident_type: "autre", severity: "modere", description: comment }).catch(() => {});
    }
    try { await apiClient.post(`/admin/missions/${missionId}/field-action`, { action, comment }); toast.success(FIELD_ACTION_LABELS[action]); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-1">Terrain</h1>
      <p className="text-sm text-muted-foreground mb-4">Missions du jour {data ? `— ${formatDate(data.date)}` : ""}</p>
      {!data ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div> :
        data.missions.length === 0 ? (
          <div data-testid="field-empty" className="bg-white rounded-2xl shadow-sm py-12 text-center text-muted-foreground">Aucune mission aujourd'hui</div>
        ) : data.missions.map((m) => (
          <div key={m.id} data-testid={`field-mission-${m.id}`} className="bg-white rounded-2xl shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-navy">{m.reference} — {m.start_time || "Heure à confirmer"}</span>
              <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1">{MISSION_STATUS_LABELS[m.status]}</span>
            </div>
            <div className="text-sm space-y-1 mb-3">
              <div><strong>{m.contact_name}</strong> {m.contact_phone && (
                <a href={`tel:${m.contact_phone}`} className="inline-flex items-center gap-1 text-orange font-semibold ml-2"><Phone className="w-3.5 h-3.5" />{m.contact_phone}</a>)}</div>
              {m.origin_address?.address_line_1 && (
                <div className="flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-navy" />
                  Départ : {m.origin_address.address_line_1}, {m.origin_address.city}</div>)}
              {m.destination_address?.address_line_1 && (
                <div className="flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-orange" />
                  Arrivée : {m.destination_address.address_line_1}, {m.destination_address.city}</div>)}
              {m.instructions && <div className="text-xs bg-graylight rounded-lg p-2 mt-1">{m.instructions}</div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {data.field_actions.map((a) => (
                <Button key={a} data-testid={`field-action-${a}-${m.id}`} onClick={() => act(m.id, a)}
                  variant={a === "incident_reported" ? "outline" : "default"}
                  className={`rounded-xl text-xs h-10 ${a === "incident_reported" ? "text-red-600 border-red-200" : "bg-navy text-white"}`}>
                  {FIELD_ACTION_LABELS[a]}
                </Button>
              ))}
            </div>
          </div>
        ))}
    </AdminGuard>
  );
}
